/**
 * Dashboard State Architecture — Step 1
 *
 * Pure, client-side derivation of the candidate dashboard's UX state.
 * Per `/app/memory/UX_DASHBOARD_STATE_ARCHITECTURE.md`:
 *
 *   1 = Just Arrived          (no identity signal)
 *   2 = Early Build           (identity, no intent / no maturity / no market)
 *   3 = Transitioning         (intent OR maturity, no market)
 *   4 = Active Market User    (market signal present — trumps all others)
 *
 * Principles:
 *   - State is DERIVED, never declared.
 *   - State is NOT persisted; recomputed on each render.
 *   - State is NEVER exposed to the user (no labels, no badges, no copy).
 *   - All predicates are simple boolean checks over already-loaded data.
 *   - No backend calls, no schema dependencies.
 */

export const DASHBOARD_STATE = Object.freeze({
  JUST_ARRIVED: 1,
  EARLY_BUILD: 2,
  TRANSITIONING: 3,
  ACTIVE_MARKET: 4,
});

const TRANSITION_DATE_STORAGE_KEY = 'st_transition_date';

function readTransitionDateFromLocalStorage() {
  try {
    const v = window.localStorage.getItem(TRANSITION_DATE_STORAGE_KEY);
    return v && typeof v === 'string' && v.length > 0 ? v : null;
  } catch {
    return null;
  }
}

/**
 * Compute the four canonical predicates.
 * Inputs are intentionally permissive — anything missing is treated as falsy.
 */
export function computeDashboardSignals({
  resume = null,
  savedJobs = [],
  applications = [],
  conversations = [],
  localStorageDate, // optional override; otherwise read from window.localStorage
} = {}) {
  const militaryRoles = resume?.military_roles || [];
  const personalInfo = resume?.personal_info || {};

  const hasIdentitySignal =
    militaryRoles.length >= 1 || Boolean(personalInfo.city);

  const transitionDate =
    typeof localStorageDate === 'string'
      ? localStorageDate
      : readTransitionDateFromLocalStorage();
  const hasIntentSignal =
    Boolean(transitionDate) || Boolean(resume?.transition_date);

  const hasMaturitySignal =
    resume?.is_complete === true && resume?.visible === true;

  const safeApplications = Array.isArray(applications) ? applications : [];
  const safeConversations = Array.isArray(conversations) ? conversations : [];
  const safeSavedJobs = Array.isArray(savedJobs) ? savedJobs : [];
  const hasMarketSignal =
    safeApplications.length >= 1 ||
    safeConversations.length >= 1 ||
    safeSavedJobs.length >= 2;

  return {
    hasIdentitySignal,
    hasIntentSignal,
    hasMaturitySignal,
    hasMarketSignal,
  };
}

/**
 * Derive the dashboard state (1..4).
 * Order of evaluation matters: market signal trumps all others.
 */
export function getDashboardState(input = {}) {
  const s = computeDashboardSignals(input);

  if (s.hasMarketSignal) return DASHBOARD_STATE.ACTIVE_MARKET;        // 4
  if (!s.hasIdentitySignal) return DASHBOARD_STATE.JUST_ARRIVED;       // 1
  if (s.hasIntentSignal || s.hasMaturitySignal) return DASHBOARD_STATE.TRANSITIONING; // 3
  return DASHBOARD_STATE.EARLY_BUILD;                                  // 2 (default for identity-only)
}

export default getDashboardState;
